import { type ReactNode } from "react";

import { type PageName, useAnalytics } from "@btnet/utilities-analytics";

export interface IAnalyticsPageContextProps {
    /**
     * The Sawmill v3 `pageName` to seed. Undefined leaves the v3 context
     * untouched, which is what routes not yet on v3 want.
     */
    pageName?: PageName;
    children?: ReactNode;
}

/**
 * Seeds the Sawmill v3 `pageName` for everything rendered beneath it.
 *
 * Mounted by each page layout (ListPageLayout, DetailPageLayout,
 * WebviewPageLayout) so a route opts into analytics with a single
 * `analyticsPageName` prop rather than every page wiring up its own context
 * provider — forgetting that wiring produced a silent null rather than an error.
 *
 * Pages still provide their own context when they need to contribute runtime
 * values a route-level seed cannot know (e.g. Bill's `billId`, LienWaiverReview's
 * `paymentToken`); nested providers merge, with the inner one winning on conflict.
 */
export function AnalyticsPageContext({ pageName, children }: IAnalyticsPageContextProps) {
    // Seeding with `{}` when no pageName is supplied keeps the hook call
    // unconditional while contributing nothing to the merged v3 context.
    const { ContextProvider } = useAnalytics(pageName ? { pageName } : {});

    return <ContextProvider>{children}</ContextProvider>;
}
